/**
 * Adapter between browser wallet extensions and the ExternalWalletProvider
 * interface expected by signing.ts.
 *
 * Detects available browser wallets (Phantom, Solflare, Backpack) and wraps
 * their signTransaction / signMessage APIs to match the serialized-bytes
 * interface the signing module expects.
 */

import type { SigningDeps } from '@morfi/backend-sdk-v2/client';
import { VersionedTransaction } from '@solana/web3.js';

// ── Browser wallet provider shapes (internal) ────────────────────────────

interface PhantomProvider {
  isPhantom?: boolean;
  signTransaction: (tx: VersionedTransaction) => Promise<VersionedTransaction>;
  signMessage: (message: Uint8Array, encoding: string) => Promise<{ signature: Uint8Array }>;
}

interface SolflareProvider {
  isSolflare?: boolean;
  signTransaction: (tx: VersionedTransaction) => Promise<VersionedTransaction>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
}

interface BackpackProvider {
  isBackpack?: boolean;
  signTransaction: (tx: VersionedTransaction) => Promise<VersionedTransaction>;
  signMessage: (message: Uint8Array) => Promise<{ signature: Uint8Array }>;
}

interface BrowserWalletWindow {
  solana?: PhantomProvider;
  solflare?: SolflareProvider;
  backpack?: BackpackProvider;
}

// ── Adapter ──────────────────────────────────────────────────────────────

type ExternalWalletProvider = ReturnType<SigningDeps['getExternalProvider']> & object;

function wrapProvider(
  signTx: (tx: VersionedTransaction) => Promise<VersionedTransaction>,
  signMsg: (message: Uint8Array, encoding: string) => Promise<{ signature: Uint8Array }>,
): ExternalWalletProvider {
  return {
    signTransaction: async (transaction: Uint8Array): Promise<{ signature: Uint8Array }> => {
      const versionedTx = VersionedTransaction.deserialize(transaction);
      const signed = await signTx(versionedTx);
      return { signature: new Uint8Array(signed.serialize()) };
    },
    signMessage: async (
      message: Uint8Array,
      encoding: string,
    ): Promise<{ signature: Uint8Array }> => {
      return signMsg(message, encoding);
    },
  };
}

/**
 * Detect and return the first available browser wallet provider, or null.
 *
 * Detection order: Phantom → Solflare → Backpack.
 */
export function getExternalWalletProvider(): ExternalWalletProvider | null {
  if (typeof window === 'undefined') return null;

  // ALLOW-UNKNOWN: window must be cast to access non-standard wallet provider properties
  const w = window as unknown as BrowserWalletWindow;

  if (w.solana?.isPhantom) {
    const phantom = w.solana;
    return wrapProvider(
      (tx) => phantom.signTransaction(tx),
      (msg, enc) => phantom.signMessage(msg, enc),
    );
  }

  if (w.solflare?.isSolflare) {
    const solflare = w.solflare;
    return wrapProvider(
      (tx) => solflare.signTransaction(tx),
      (msg, _enc) => solflare.signMessage(msg),
    );
  }

  if (w.backpack?.isBackpack) {
    const backpack = w.backpack;
    return wrapProvider(
      (tx) => backpack.signTransaction(tx),
      (msg, _enc) => backpack.signMessage(msg),
    );
  }

  return null;
}
